import { queryOptions, useQuery } from "@tanstack/react-query";

import {
  CAR_CONDITIONS,
  CAR_STATUSES,
  getConditionsFromCars,
  getDataFilteredAndSorted,
  getStatusesFromCars,
  mapCarPayloadToCar,
} from "./utils";

import { env } from "@/config/env";
import { api } from "@/lib/api-client";
import { hasAccessToken } from "@/lib/auth";

import type { QueryConfig } from "@/lib/react-query";
import type { Car, CarPayload } from "@/types/car";
import type { PaginatedList } from "@/types/entity";
import type { CarConditions, CarStatuses, QueryParams } from "./types";

export const getFormerCars = async ({
  page,
  pageSize,
}: QueryParams): Promise<PaginatedList<Car>> => {
  const response = (await api.get(
    `/carburate/cars/archived/?page=${page}&page_size=${pageSize}`
  )) as PaginatedList<CarPayload>;
  return {
    ...response,
    results: response.results.map(mapCarPayloadToCar),
  } as PaginatedList<Car>;
};

export const getFormerCarsQueryOptions = ({ page, pageSize }: QueryParams = {}) => {
  return queryOptions({
    queryKey: ["former-cars", { page, pageSize }],
    queryFn: () => getFormerCars({ page, pageSize }),
  });
};

type UseFormerCarsOptions = QueryParams & {
  queryConfig?: QueryConfig<typeof getFormerCars>;
};

export const useFormerCars = ({
  page = 1,
  pageSize = 25,
  parameters,
  queryConfig,
  sort,
}: UseFormerCarsOptions = {}) => {
  const isClientPagination = env.PAGINATION === "client";

  const currentPage = isClientPagination ? 1 : page;
  const currentPageSize = isClientPagination ? 999999 : pageSize;

  let data: PaginatedList<Car>;
  let conditions: CarConditions = CAR_CONDITIONS;
  let statuses: CarStatuses = CAR_STATUSES;

  const {
    data: response,
    isSuccess,
    ...props
  } = useQuery({
    enabled: hasAccessToken(),
    ...getFormerCarsQueryOptions({ page: currentPage, pageSize: currentPageSize }),
    ...queryConfig,
  });

  data = response!;
  if (isClientPagination && isSuccess) {
    conditions = getConditionsFromCars(data.results);
    statuses = getStatusesFromCars(data.results);
    data = getDataFilteredAndSorted(data, { parameters, page, pageSize, sort });
  }

  return { data, conditions, statuses, isSuccess, ...props };
};
